import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

import ErrorMessage from '../../../components/Form/ErrorMessage'
import FileDetailsPreview from '../../../components/FileDetailsPreview'
import FileUploadInput from '../../../components/Form/FileUploadInput'

import useFileUpload from '../../../features/hooks/useFileUpload'

import { showToasts } from '../../../helpers/toasts'

import { fileUpload } from '../../../modules/actions/files'

import { TOAST_TYPE } from '../../../constants/other'

const SingleFileUpload = ({
  fileName,
  fileURL,
  onFileRemove,
  onFileUploaded,
  // fileValidationHandler should be a function returning promise
  fileValidationHandler,
  fileUploadOptions,
  filePreviewType = 'image',
  error,
  maxSize,
  accept,
  filePreviewClassName,
  uploadButtonClassName,
  uploadBtnLabelClassName
}) => {
  const dispatch = useDispatch()

  const {
    selectedFile,
    setSelectedFile,
    fileURLBlob,
    setFileURLBlob,
    isFileLoading,
    fileIsUploaded,
    fileUploadProgress,
    handleRemoveFile
  } = useFileUpload({
    onFileRemove,
    onFileUploaded
  })

  const showPreview = isFileLoading || fileIsUploaded || fileURL

  const startFileUploading = useCallback(
    file => {
      dispatch(fileUpload(file, fileUploadOptions))

      setSelectedFile(file)
      setFileURLBlob(URL.createObjectURL(file))
    },
    [dispatch, fileUploadOptions, setFileURLBlob, setSelectedFile]
  )

  const handleFileChange = useCallback(
    file => {
      if (fileValidationHandler) {
        fileValidationHandler(file)
          .then(() => startFileUploading(file))
          .catch(error => {
            showToasts({
              type: TOAST_TYPE.error,
              message: error.message
            })
          })
      } else {
        startFileUploading(file)
      }
    },
    [fileValidationHandler, startFileUploading]
  )

  return (
    <>
      {showPreview ? (
        <FileDetailsPreview
          key={selectedFile.name || fileName}
          fileName={selectedFile.name || fileName}
          isFileLoading={isFileLoading}
          fileUploadProgress={fileUploadProgress}
          fileUrl={fileURLBlob || fileURL}
          allowDelete={!!fileURL}
          filePreviewType={filePreviewType}
          removeFile={handleRemoveFile}
          className={filePreviewClassName}
        />
      ) : (
        <>
          <FileUploadInput
            name="single_file_upload"
            onFileUpload={handleFileChange}
            accept={accept}
            maxSize={maxSize}
            uploadButtonClassName={uploadButtonClassName}
            uploadBtnLabelClassName={uploadBtnLabelClassName}
          />
          {error && <ErrorMessage error={error} />}
        </>
      )}
    </>
  )
}

SingleFileUpload.propTypes = {
  fileName: PropTypes.string,
  fileURL: PropTypes.string,
  onFileRemove: PropTypes.func,
  onFileUploaded: PropTypes.func,
  fileValidationHandler: PropTypes.func,
  fileUploadOptions: PropTypes.object,
  filePreviewType: PropTypes.string,
  error: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.string,
  filePreviewClassName: PropTypes.string,
  uploadButtonClassName: PropTypes.string,
  uploadBtnLabelClassName: PropTypes.string
}

export default SingleFileUpload
